/**
 * Overview:
 * This Redux slice manages application configuration and settings.
 * It loads common configuration, widget-specific settings, and user
 * impersonation information from backend services, while providing
 * selectors and state management utilities for accessing configuration
 * values throughout the application.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getCommonChanges } from "@/services/commonconfig";
import { getwidgetConfigChanges } from "@/services/widgetConfig";
import { getCurrentImpersonation } from "@/services/impersonation";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export type WidgetConfigurations = {
  [featureCode: string]: {
    [configKey: string]: any;
  };
};

export type CommonConfig = {
  encryptionKey: string;
  LogoutTime: number;
  WarningTime: number;
  InactiveDays: number;

  TourIcon: boolean;
  NotificationIcon: boolean;
  LanguageSwitchIcon: boolean;

  HighContrast: boolean;
  UnderlineLinks: boolean;
  TextSpacing: boolean;

  ReduceMotion: boolean;

  KeyboardNavigation: boolean;
  FocusIndicators: boolean;

  CursorSize: boolean;

  TTS: boolean;
  SpeechRate: boolean;
  TTSOnFocus: boolean;
  TTSOnHover: boolean;

  ScreenReaderOptimization: boolean;
  DyslexiaFriendlyFont: boolean;

  LightTheme: boolean;
  DarkTheme: boolean;
  VibrantTheme: boolean;

  AllProgramsVisibility: string;
  FluxxURL: string;
  PowerBIURL: string;

  translationDbEnabled: boolean;
};

type ImpersonationState = {
  isImpersonating: boolean;
  impersonationId: string | null;

  actualUpn: string | null;
  effectiveUpn: string | null;
};

type ConfigState = {
  commonConfig: CommonConfig | null;
  widgetConfigurations: WidgetConfigurations | null;

  impersonation: ImpersonationState;

  status: RequestStatus;
  error: string | null;
};

const initialState: ConfigState = {
  commonConfig: null,
  widgetConfigurations: null,

  impersonation: {
    isImpersonating: false,
    impersonationId: null,
    actualUpn: null,
    effectiveUpn: null,
  },

  status: "idle",
  error: null,
};

export const fetchImpersonation =
  createAsyncThunk(
    "config/fetchImpersonation",
    async () => {
      return await getCurrentImpersonation();
    }
  );
  
export const fetchWidgetConfigurations = createAsyncThunk<
  WidgetConfigurations | null,
  void
>(
  "config/fetchWidgetConfigurations",
  async () => {
    const res = await getwidgetConfigChanges();
    return res;
  }
);

export const fetchCommonConfig = createAsyncThunk<CommonConfig | null, void>(
  "config/fetchCommonConfig",
  async () => {
    const res = await getCommonChanges();

    return res;
  }
);

let translationDbEnabled = false;

export const setTranslationDbEnabled = (value: boolean) => {
  translationDbEnabled = value;
};

export const isTranslationDbEnabled = () => {
  return translationDbEnabled;
};

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
   resetConfigState(state) {
  state.commonConfig = null;
  state.widgetConfigurations = null;
  state.status = "idle";
  state.error = null;
  setTranslationDbEnabled(false);
},
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommonConfig.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchWidgetConfigurations.fulfilled, (state, action) => {
  state.widgetConfigurations = action.payload;
})
.addCase(
  fetchImpersonation.fulfilled,
  (state, action) => {
    state.impersonation =
      action.payload;
  }
)

      .addCase(fetchCommonConfig.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.commonConfig = action.payload;
          setTranslationDbEnabled(
    action.payload?.translationDbEnabled ?? false
  );
      })

      .addCase(fetchCommonConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load common config";
      });
      
  },
});

export const { resetConfigState } = configSlice.actions;

export const selectCommonConfig = (s: any): CommonConfig | null => s.config.commonConfig;

export const selectWidgetConfigurations = (s: any) =>
  s.config.widgetConfigurations;

// Individual flags

export const selectTourIcon = (s: any): boolean => s.config.commonConfig?.TourIcon ?? false;

export const selectNotificationIcon = (s: any): boolean =>
  s.config.commonConfig?.NotificationIcon ?? false;

export const selectLanguageSwitchIcon = (s: any): boolean =>
  s.config.commonConfig?.LanguageSwitchIcon ?? false;

export const selectLogoutTime = (s: any): number => s.config.commonConfig?.LogoutTime ?? 2;

export const selectWarningTime = (s: any): number => s.config.commonConfig?.WarningTime ?? 1;

export const selectInactiveDays = (s: any): number => s.config.commonConfig?.InactiveDays ?? 100;

export const selectFluxxURL = (s: any): string => s.config.commonConfig?.FluxxURL ?? "";

export const selectPowerBIURL = (s: any): string => s.config.commonConfig?.PowerBIURL ?? "";

export const selectNotificationUrl1 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.notifications"]
    ?.NotificationFluxx1 ?? "";

export const selectNotificationUrl2 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.notifications"]
    ?.NotificationFluxx2 ?? "";

export const selectTaskPrioritiesUrl1 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.taskpriorities"]
    ?.TaskPrioritiesFluxx1 ?? "";

export const selectTaskPrioritiesUrl2 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.taskpriorities"]
    ?.TaskPrioritiesFluxx2 ?? "";

export const selectPowerBIOversightUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.oversight"]
    ?.PowerBIOversightUrl ?? "";

export const selectPowerBIFinancialsUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.impacttargets"]
    ?.PowerBIFinancialsUrl ?? "";

export const selectPowerBIAnalyticsUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.analyticsinsights"]
    ?.PowerBIAnalyticsUrl ?? "";

export const selectPowerBIIdeaTrackerUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.ideatracker"]
    ?.PowerBIIdeaTrackerUrl ?? "";


export const selectPowerBIProgramImpactUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.programimpact"]
    ?.PowerBIProgramImpactUrl ?? "";

export const selectDisbursementFluxxUrl1 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.disbursementrequest"]
    ?.DisbursementFluxx1 ?? "";

export const selectDisbursementFluxxUrl2 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.disbursementrequest"]
    ?.DisbursementFluxx2 ?? "";

export const selectPartnerDirectoryFluxxUrl1 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.partnerdirectory"]
    ?.PartnerDirectoryFluxx1 ?? "";

export const selectPartnerDirectoryFluxxUrl2 = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.partnerdirectory"]
    ?.PartnerDirectoryFluxx2 ?? "";

export const selectOutlookMeetingsUrl = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.upcomingmeetings"]
    ?.OutlookMeetingsUrl ?? "";

export const selectAllProgramsVisibility = (s: any): string =>
  s.config.widgetConfigurations?.["dashboard.myactiveitems"]
    ?.AllProgramsVisibility ?? "{}";

export const selectImpersonation =
  (s: any) => s.config.impersonation;

export const selectIsImpersonating =
  (s: any) =>
    s.config.impersonation
      ?.isImpersonating;

export const selectEffectiveUpn =
  (s: any) =>
    s.config.impersonation
      ?.effectiveUpn;

export default configSlice.reducer;
